'use client'

import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

/**
 * One treasury holding, exactly as /api/treasury/tokens returns it. `balance`
 * is the raw on-chain amount; pair it with `decimals` to display.
 */
export type TokenAccount = {
  tokenAccount: string
  mint: string
  balance: string
  decimals: number
  image: string
  symbol: string
  name: string
  price: number | null
  program: 'spl' | 'spl-2022'
}

type TreasuryTokensResponse = {
  tokens: TokenAccount[]
  prices: Record<string, number>
  updatedAt: string
}

const useTokensHook = () => {
  const [tokenAccounts, setTokenAccounts] = useState<TokenAccount[]>([])
  const [prices, setPrices] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // One request for the whole tab: the route batches the RPC, metadata and
  // price lookups that used to run per token in the browser.
  const fetchTokenAccounts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/treasury/tokens')
      const data = (await res.json()) as TreasuryTokensResponse & {
        error?: string
      }

      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`)

      setTokenAccounts(data.tokens || [])
      setPrices(data.prices || {})
    } catch (e) {
      console.error('Failed to fetch treasury tokens:', e)
      setError(e instanceof Error ? e.message : 'Failed to fetch tokens')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTokenAccounts()
  }, [fetchTokenAccounts])

  return {
    tokenAccounts,
    prices,
    loading,
    error,
    refetch: fetchTokenAccounts,
    fetchTokenAccounts,
  }
}

export type TokensManager = ReturnType<typeof useTokensHook>

const TokensContext = createContext<TokensManager | null>(null)

const { Provider } = TokensContext

const TokensProvider = ({ children }: { children: ReactNode }) => {
  return <Provider value={useTokensHook()}>{children}</Provider>
}

const useTokens = (): TokensManager => {
  const context = useContext(TokensContext)
  if (!context) {
    throw new Error('useTokens has to be used within <TokensProvider>')
  }
  return context
}
export { useTokens, TokensProvider }
